/**
 * API Service Layer
 *
 * Provides clean abstraction for all backend API calls.
 * Handles authentication, error handling, and request/response formatting.
 */

import { extractSubscriptionList } from '../utils/subscription';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8002';

/** Base URL of the SPA (HashRouter: origin + /#) for backend redirects and Stripe return URLs */
export function getClientFrontendBaseUrl() {
  if (typeof window === 'undefined' || !window.location?.origin) {
    return '';
  }
  const fromEnv = process.env.REACT_APP_FRONTEND_BASE_URL;
  if (fromEnv && typeof fromEnv === 'string' && fromEnv.trim()) {
    return fromEnv.trim().replace(/\/$/, '');
  }
  return `${window.location.origin}/#`;
}

class APIService {
  /**
   * Get authorization headers with current token and client frontend origin
   */
  getAuthHeaders() {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Token ${token}` }),
    };
    const frontendBase = getClientFrontendBaseUrl();
    if (frontendBase) {
      headers['X-Frontend-URL'] = frontendBase;
    }
    return headers;
  }

  /**
   * DRF often returns { detail: "Invalid token." } for bad/expired auth tokens.
   */
  isInvalidTokenPayload(data) {
    const d = data?.detail;
    if (typeof d !== 'string') return false;
    const n = d.trim().toLowerCase().replace(/\.$/, '');
    return n === 'invalid token';
  }

  /**
   * Clear session and go to login (HashRouter).
   */
  clearSessionAndGoLogin() {
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('user');
    localStorage.removeItem('currentOrganizationId');
    localStorage.removeItem('selectedProjectId');
    const projectSelectionKeys = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith('selectedProjectId:')) {
        projectSelectionKeys.push(key);
      }
    }
    projectSelectionKeys.forEach((key) => localStorage.removeItem(key));
    const deleteCookie = (name) => {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
    };
    deleteCookie('user_session');
    deleteCookie('auth_token');
    window.location.replace('/#/login');
  }

  /**
   * Parse JSON body and redirect to login if token is invalid.
   * On error, throws { response: { status, data } } for handleError.
   */
  async parseJsonResponse(response) {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      if (this.isInvalidTokenPayload(data)) {
        this.clearSessionAndGoLogin();
        throw new Error('Session expired');
      }
      const d = data;

      // DRF throttle hit. Surface `code: 'throttled'` + a numeric
      // `retryAfter` (seconds) so UIs can disable submit + show a
      // human-friendly cooldown rather than the raw DRF text.
      // Header is the source of truth; the message in the body
      // is a fallback for envs where Retry-After is stripped.
      if (response.status === 429) {
        const headerVal = response.headers?.get?.('Retry-After');
        let retryAfter = Number(headerVal);
        if (!Number.isFinite(retryAfter) || retryAfter < 0) {
          const m = /(\d+)\s*seconds?/i.exec(typeof d?.detail === 'string' ? d.detail : '');
          retryAfter = m ? Number(m[1]) : 60;
        }
        const err = new Error('Too many requests. Please wait and try again.');
        err.response = { status: 429, data };
        err.code = 'throttled';
        err.retryAfter = retryAfter;
        throw err;
      }

      const msg =
        (typeof d?.error === 'string' && d.error) ||
        (typeof d?.detail === 'string' && d.detail) ||
        (Array.isArray(d?.non_field_errors) && d.non_field_errors.filter(Boolean).join(' ')) ||
        'Request failed';
      const err = new Error(msg);
      err.response = { status: response.status, data };
      throw err;
    }
    return data;
  }

  /**
   * Handle API errors consistently
   */
  handleError(error, defaultMessage = 'An error occurred') {
    if (error.response) {
      if (this.isInvalidTokenPayload(error.response.data)) {
        this.clearSessionAndGoLogin();
        throw new Error('Session expired');
      }
      // Server responded with error status
      const d = error.response.data;
      let message =
        d?.message ||
        d?.error ||
        (typeof d?.detail === 'string' ? d.detail : null);
      if (!message && Array.isArray(d?.non_field_errors)) {
        message = d.non_field_errors.filter(Boolean).join(' ');
      }
      if (!message) message = defaultMessage;
      const err = new Error(message);
      err.response = error.response;
      throw err;
    } else if (error.request) {
      // Request made but no response received
      throw new Error('No response from server. Please check your connection.');
    } else {
      // Error setting up request
      throw new Error(error.message || defaultMessage);
    }
  }

  // ==================== AUTH (authenticated) ====================

  /**
   * Change password for the current user (requires Token auth).
   */
  async changePassword({ currentPassword, newPassword, newPasswordConfirm }) {
    const response = await fetch(`${API_BASE_URL}/api/users/change-password/`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: newPasswordConfirm,
      }),
    });
    return await this.parseJsonResponse(response);
  }

  // ==================== ORGANIZATIONS ====================

  /**
   * Get all organizations for current user
   */
  async getOrganizations() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/organizations/`, {
        headers: this.getAuthHeaders()
      });
      return await this.parseJsonResponse(response);
    } catch (error) {
      console.error('getOrganizations error:', error);
      this.handleError(error, 'Failed to fetch organizations');
      return []; // Return empty array instead of undefined
    }
  }

  /**
   * Create new organization
   */
  async createOrganization(data) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/organizations/`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return await this.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to create organization');
    }
  }

  /**
   * Get organization details
   */
  async getOrganization(orgId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/organizations/${orgId}/`, {
        headers: this.getAuthHeaders()
      });
      return await this.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to fetch organization');
    }
  }

  /**
   * Update organization
   */
  async updateOrganization(orgId, data) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/organizations/${orgId}/`, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return await this.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to update organization');
    }
  }

  // ==================== ORGANIZATION INVITES ====================

  async listInvites(orgId) {
    try {
      const url = orgId
        ? `${API_BASE_URL}/api/projects/organization-invites/?organization=${orgId}`
        : `${API_BASE_URL}/api/projects/organization-invites/`;
      const response = await fetch(url, { headers: this.getAuthHeaders() });
      const data = await this.parseJsonResponse(response);
      return Array.isArray(data) ? data : data?.results || [];
    } catch (error) {
      this.handleError(error, 'Failed to fetch invites');
      return [];
    }
  }

  async createInvite(orgId, { email, role }) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/projects/organization-invites/`,
        {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: JSON.stringify({ organization: orgId, email, role }),
        }
      );
      return await this.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to send invite');
    }
  }

  async revokeInvite(inviteId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/projects/organization-invites/${inviteId}/revoke/`,
        { method: 'POST', headers: this.getAuthHeaders() }
      );
      return await this.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to revoke invite');
    }
  }

  async resendInvite(inviteId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/projects/organization-invites/${inviteId}/resend/`,
        { method: 'POST', headers: this.getAuthHeaders() }
      );
      return await this.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to resend invite');
    }
  }

  async acceptTerms({ wantsNewsletter } = {}) {
    // POST /api/auth/accept-terms/ — stamps Terms acceptance, optionally
    // updates the user's newsletter preference. Used by the welcome-terms
    // interstitial (after a Login-page Continue-with-Google flow created
    // a fresh account).
    const body = {};
    if (wantsNewsletter !== undefined) body.wants_newsletter = !!wantsNewsletter;
    const response = await fetch(`${API_BASE_URL}/api/auth/accept-terms/`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(body),
    });
    return await this.parseJsonResponse(response);
  }

  async removeMember(memberUuid) {
    // Backend: DELETE /api/projects/organization-members/<uuid>/
    // 204 on success; 400 with code='cannot_remove_owner' /
    // 'cannot_remove_self' for the disallowed cases; 403 if requester
    // isn't the org owner.
    const response = await fetch(
      `${API_BASE_URL}/api/projects/organization-members/${memberUuid}/`,
      { method: 'DELETE', headers: this.getAuthHeaders() }
    );
    if (response.status === 204) return true;
    return await this.parseJsonResponse(response);
  }

  async getInvitePreview(token) {
    // Public endpoint — no auth required, but harmless to send headers if present.
    const response = await fetch(`${API_BASE_URL}/api/invites/${token}/`);
    return await this.parseJsonResponse(response);
  }

  async acceptInvite(token) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/invites/${token}/accept/`,
        { method: 'POST', headers: this.getAuthHeaders() }
      );
      return await this.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to accept invite');
    }
  }

  /**
   * Register a new user via an invite token. Server forces the user's
   * email to match the invite's email (no spoofing) and auto-accepts
   * the invite atomically. On success the response carries:
   *   { user, token, organization_id, organization_name, invite_accepted, ... }
   * Used by the inline signup form on /invites/accept/<token> so the
   * recipient never has to leave the page or contend with Google OAuth
   * grabbing their primary Google email.
   */
  async registerWithInvite({ invite_token, email, name, password, password_confirm, terms_accepted = true }) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/register/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invite_token, email, name, password, password_confirm, terms_accepted }),
      });
      return await this.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to create account');
    }
  }

  // ==================== ACCOUNT ====================

  async deleteAccount() {
    const response = await fetch(`${API_BASE_URL}/api/auth/account/`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      const body = await response.text();
      throw new Error(body || `Delete account failed (${response.status})`);
    }
    return true;
  }

  /**
   * Get user's default organization (simplified flow)
   */
  async getDefaultOrganization() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/organizations/default/`, {
        headers: this.getAuthHeaders()
      });
      return await this.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to fetch default organization');
    }
  }

  // ==================== PLANS & SUBSCRIPTIONS ====================

  /**
   * Get all available plans
   */
  async getPlans() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/billing/plans/`, {
        headers: this.getAuthHeaders()
      });
      const data = await this.parseJsonResponse(response);
      return extractSubscriptionList(data);
    } catch (error) {
      this.handleError(error, 'Failed to fetch plans');
    }
  }

  /**
   * Get organization's active subscription
   */
  async getSubscription(orgId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/billing/subscriptions/?organization=${orgId}`, {
        headers: this.getAuthHeaders()
      });
      const raw = await this.parseJsonResponse(response);
      const subscriptions = extractSubscriptionList(raw);
      return subscriptions.find((sub) => sub.active) || null;
    } catch (error) {
      this.handleError(error, 'Failed to fetch subscription');
    }
  }

  /**
   * Create or update subscription for organization
   */
  async updateSubscription(orgId, planId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/billing/subscriptions/`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          organization: orgId,
          plan: planId,
          active: true
        })
      });
      return await this.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to update subscription');
    }
  }

  /**
   * Sync subscription from Stripe checkout session
   */
  async syncSubscription(sessionId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/billing/stripe/sync-subscription/`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          session_id: sessionId
        })
      });
      return await this.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to sync subscription');
      throw error;
    }
  }

  /**
   * Get subscription billing history for organization
   */
  async getSubscriptionHistory(orgId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/billing/subscription-history/?organization=${orgId}`, {
        headers: this.getAuthHeaders()
      });
      return await this.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to fetch subscription history');
      return [];
    }
  }

  /**
   * Get subscription history summary
   */
  async getSubscriptionHistorySummary(orgId = null) {
    try {
      const url = orgId 
        ? `${API_BASE_URL}/api/billing/subscription-history/summary/?organization=${orgId}`
        : `${API_BASE_URL}/api/billing/subscription-history/summary/`;
      
      const response = await fetch(url, {
        headers: this.getAuthHeaders()
      });
      return await this.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to fetch subscription history summary');
      return null;
    }
  }

  // ==================== CHAINS ====================

  /**
   * Get all available blockchain chains
   */
  async getChains() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/chains/chains/`, {
        headers: this.getAuthHeaders()
      });
      return await this.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to fetch chains');
    }
  }

  /**
   * Get chains accessible to organization
   */
  async getOrganizationChains(orgId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/organization-chain-access/?organization=${orgId}`, {
        headers: this.getAuthHeaders()
      });
      return await this.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to fetch organization chains');
    }
  }

  /**
   * Grant chain access to organization
   */
  async grantChainAccess(orgId, chainId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/organization-chain-access/`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          organization: orgId,
          chain: chainId,
          enabled: true
        })
      });
      return await this.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to grant chain access');
    }
  }

  // ==================== PROJECTS ====================

  /**
   * Get all projects for an organization
   */
  async getProjects(orgId = null) {
    try {
      const url = orgId
        ? `${API_BASE_URL}/api/projects/projects/?organization=${orgId}`
        : `${API_BASE_URL}/api/projects/projects/`;

      const response = await fetch(url, {
        headers: this.getAuthHeaders()
      });
      return await this.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to fetch projects');
    }
  }

  /**
   * Create new project
   */
  async createProject(data) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/projects/`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return await this.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to create project');
    }
  }

  /**
   * Delete project
   */
  async deleteProject(projectId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/projects/${projectId}/`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      await this.parseJsonResponse(response);
      return true;
    } catch (error) {
      this.handleError(error, 'Failed to delete project');
    }
  }

  /**
   * Add chain to project
   */
  async addChainToProject(projectId, chainId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/projects/${projectId}/add_chain/`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          chain_id: chainId,
          enabled: true
        })
      });
      return await this.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to add chain to project');
    }
  }

  /**
   * Remove chain from project
   */
  async removeChainFromProject(projectId, chainId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/projects/${projectId}/remove_chain/`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          chain_id: chainId
        })
      });
      await this.parseJsonResponse(response);
      return true;
    } catch (error) {
      this.handleError(error, 'Failed to remove chain from project');
    }
  }

  /**
   * Toggle project chain access (enable/disable)
   */
  async toggleProjectChainAccess(chainAccessId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/chain-access/${chainAccessId}/toggle/`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      });
      return await this.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to toggle chain access');
    }
  }

  /**
   * Get project details with chain access
   */
  async getProject(projectId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/projects/${projectId}/`, {
        headers: this.getAuthHeaders()
      });
      return await this.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to fetch project');
    }
  }

  // ==================== API KEYS ====================

  /**
   * Get all API keys for an organization
   */
  async getApiKeys(orgId = null, projectId = null) {
    try {
      let url = `${API_BASE_URL}/api/chains/api-keys/`;
      const params = new URLSearchParams();
      if (orgId) params.append('organization', orgId);
      if (projectId) params.append('project', projectId);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        headers: this.getAuthHeaders()
      });
      return await this.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to fetch API keys');
    }
  }

  /**
   * Create new API key
   */
  async createApiKey(data) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/chains/api-keys/`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return await this.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to create API key');
    }
  }

  /**
   * Delete API key
   */
  /**
   * Rotate an API key — backend POST /api/chains/api-keys/<uuid>/regenerate/
   * Returns the updated ApiKey object with the NEW key value. Old key is
   * dead the moment the backend saves. UI must surface the new value
   * once and warn that it can't be retrieved again.
   *
   * Use case: the customer's key got leaked / committed to git / posted in
   * a screenshot. They want a fresh value without losing analytics history.
   */
  async rotateApiKey(keyId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/chains/api-keys/${keyId}/regenerate/`,
        { method: 'POST', headers: this.getAuthHeaders() }
      );
      return await this.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to rotate API key');
    }
  }

  async deleteApiKey(keyId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/chains/api-keys/${keyId}/`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      await this.parseJsonResponse(response);
      return true;
    } catch (error) {
      this.handleError(error, 'Failed to delete API key');
    }
  }

  /**
   * Update API key details
   */
  async updateApiKey(keyId, data) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/chains/api-keys/${keyId}/`, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return await this.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to update API key');
    }
  }

  /**
   * Get API key usage statistics
   */
  async getApiKeyUsage(keyId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/chains/api-keys/${keyId}/usage/`, {
        headers: this.getAuthHeaders()
      });
      return await this.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to fetch API key usage');
    }
  }

  /**
   * Get API key usage analytics with time-series data
   * @param {number} keyId - API key ID
   * @param {string} period - Time period: '1d', '7d', or '30d'
   */
  async getApiKeyAnalytics(keyId, period = '30d') {
    try {
      const response = await fetch(`${API_BASE_URL}/api/chains/api-keys/${keyId}/analytics/?period=${period}`, {
        headers: this.getAuthHeaders()
      });
      return await this.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to fetch API key analytics');
    }
  }

  // ==================== CREDIT WALLET (ADR-0002) ====================

  /**
   * GET /api/billing/credit-wallet/me/
   *
   * Returns the wallet for the requester's org. 404 on first-time orgs
   * (no wallet row yet) — caller should treat 404 as "zero state".
   * Auto-recharge fields are scrubbed server-side for non-owners.
   */
  async getCreditWallet(orgId) {
    const qs = orgId ? `?organization=${encodeURIComponent(orgId)}` : '';
    const response = await fetch(`${API_BASE_URL}/api/billing/credit-wallet/me/${qs}`, {
      headers: this.getAuthHeaders(),
    });
    return await this.parseJsonResponse(response);
  }

  /**
   * PATCH /api/billing/credit-wallet/me/
   *
   * Owner-only writes (server enforces). Pass a partial payload of any
   * mutable field: `auto_recharge_enabled`, `monthly_spend_cap_cents`,
   * `auto_recharge_threshold_cents`, `auto_recharge_amount_cents`,
   * `auto_recharge_payment_method_id`.
   */
  async patchCreditWallet(orgId, patch) {
    const qs = orgId ? `?organization=${encodeURIComponent(orgId)}` : '';
    const response = await fetch(`${API_BASE_URL}/api/billing/credit-wallet/me/${qs}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(patch || {}),
    });
    return await this.parseJsonResponse(response);
  }

  /**
   * POST /api/billing/credit-topup/
   *
   * Owner-only. Creates a Stripe Checkout Session and returns
   * `{ url, session_id, amount_cents, min_topup_cents, max_topup_cents }`.
   * Caller should redirect window to `url` (Stripe Checkout).
   */
  async createCreditTopup(orgId, amountCents) {
    const body = { amount_cents: Math.floor(Number(amountCents) || 0) };
    if (orgId) body.organization_id = orgId;
    const response = await fetch(`${API_BASE_URL}/api/billing/credit-topup/`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(body),
    });
    return await this.parseJsonResponse(response);
  }

  // ==================== GATEWAY ENDPOINTS ====================

  /**
   * Validate API key
   */
  async validateApiKey(apiKey, chainSlug) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/gateway/validate-key/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
          'x-chain': chainSlug
        }
      });
      return await this.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to validate API key');
    }
  }

  /**
   * Check rate limits for API key
   */
  async checkRateLimit(apiKey) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/gateway/check-rate-limit/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey
        }
      });
      return await this.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to check rate limits');
    }
  }
}

const apiService = new APIService();
export default apiService;
